import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Typography,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../../app/api/cloudApi';

export default function WLANSettingsTab({ deviceId, siteId, accountId, token }) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    opMode: 0,
    authType: 0,
    encType: 0,
    essid: '',
    authKey: '',
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (deviceId && token) {
      loadConfig();
    }
  }, [deviceId, token]);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cloudApi.getWLANConfig(token, siteId, accountId, deviceId);
      if (response.data) {
        setConfig(response.data);
      }
    } catch (err) {
      console.error('Error loading WLAN config:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to load WLAN configuration';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await cloudApi.setWLANConfig(token, siteId, accountId, {
        deviceId: deviceId,
        ...config,
      });
      enqueueSnackbar('WLAN configuration saved successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error saving WLAN config:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to save WLAN configuration';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <Alert severity="info">
          Configure wireless network settings for the device. Note: Changes will require device reboot to take effect.
        </Alert>

        <FormControlLabel
          control={
            <Switch
              checked={config.enabled}
              onChange={(e) => handleFieldChange('enabled', e.target.checked)}
            />
          }
          label="Enable Wireless Network"
        />

        {config.enabled && (
          <>
            <TextField
              fullWidth
              label="ESSID (Network Name)"
              value={config.essid}
              onChange={(e) => handleFieldChange('essid', e.target.value)}
              placeholder="MyWiFiNetwork"
              helperText="The name of the wireless network to connect to"
              inputProps={{ maxLength: 32 }}
            />

            <FormControl fullWidth>
              <InputLabel id="op-mode-label">Operation Mode</InputLabel>
              <Select
                labelId="op-mode-label"
                value={config.opMode}
                label="Operation Mode"
                onChange={(e) => handleFieldChange('opMode', e.target.value)}
              >
                <MenuItem value={0}>Managed (Infrastructure)</MenuItem>
                <MenuItem value={1}>Ad-hoc (Peer-to-peer)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="auth-type-label">Authentication Type</InputLabel>
              <Select
                labelId="auth-type-label"
                value={config.authType}
                label="Authentication Type"
                onChange={(e) => handleFieldChange('authType', e.target.value)}
              >
                <MenuItem value={0}>Open</MenuItem>
                <MenuItem value={1}>Shared</MenuItem>
                <MenuItem value={2}>WPA-PSK</MenuItem>
                <MenuItem value={3}>WPA2-PSK</MenuItem>
              </Select>
            </FormControl>

            {config.authType > 0 && (
              <>
                <FormControl fullWidth>
                  <InputLabel id="enc-type-label">Encryption Type</InputLabel>
                  <Select
                    labelId="enc-type-label"
                    value={config.encType}
                    label="Encryption Type"
                    onChange={(e) => handleFieldChange('encType', e.target.value)}
                  >
                    <MenuItem value={0}>None</MenuItem>
                    <MenuItem value={1}>WEP</MenuItem>
                    <MenuItem value={2}>TKIP/AES</MenuItem>
                    <MenuItem value={3}>AES</MenuItem>
                    <MenuItem value={4}>TKIP</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="Authentication Key (Password)"
                  value={config.authKey}
                  onChange={(e) => handleFieldChange('authKey', e.target.value)}
                  placeholder="Enter network password"
                  helperText="The password for the wireless network"
                  inputProps={{ maxLength: 64 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? (
                            <Icon icon="solar:eye-bold" />
                          ) : (
                            <Icon icon="solar:eye-closed-bold" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}

            <Alert severity="warning">
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Important Notes:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Ensure the wireless network is available and stable before connecting</li>
                <li>The device will need to reboot after saving wireless settings</li>
                <li>If connection fails, the device will fall back to wired connection</li>
                <li>WPA2-PSK with AES encryption is recommended for security</li>
              </Typography>
            </Alert>
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={loadConfig} disabled={loading || saving}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Icon icon="solar:diskette-bold" />}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

